import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  CreditPolicy,
  CreateCreditPolicyInput,
  CreditScore,
  CapacityAnalysis,
  SimulationParams,
  SimulationResult,
  AmortizationSchedule,
  Loan,
  LoanRequestInput,
  LoanFilters,
  PaginatedLoans,
  EarlyPayoffResult,
  EligibilityResult,
  CollectionRule,
  CreateCollectionRuleInput,
  Renegotiation,
  CreateRenegotiationInput,
  AgingReport,
  PDDReport,
  CreditDashboard,
  DashboardFilters,
  CreditProductType,
} from '../types/credit';

// ==================== Credit Score ====================

export function useCreditScore(customerId: string, forceRefresh = false) {
  return useQuery({
    queryKey: ['credit-score', customerId, forceRefresh],
    queryFn: () =>
      api
        .get<CreditScore>(`/credit/score/${customerId}`, {
          params: { forceRefresh },
        })
        .then((r) => r.data),
    enabled: !!customerId,
  });
}

export function useCapacityAnalysis(customerId: string) {
  return useQuery({
    queryKey: ['credit-capacity', customerId],
    queryFn: () =>
      api
        .get<CapacityAnalysis>(`/credit/score/${customerId}/capacity`)
        .then((r) => r.data),
    enabled: !!customerId,
  });
}

// ==================== Credit Policies ====================

export function useCreditPolicies(filters?: {
  productType?: CreditProductType;
  isActive?: boolean;
}) {
  return useQuery({
    queryKey: ['credit-policies', filters],
    queryFn: () =>
      api
        .get<CreditPolicy[]>('/credit/policies', { params: filters })
        .then((r) => r.data),
  });
}

export function useCreditPolicy(policyId: string) {
  return useQuery({
    queryKey: ['credit-policy', policyId],
    queryFn: () =>
      api.get<CreditPolicy>(`/credit/policies/${policyId}`).then((r) => r.data),
    enabled: !!policyId,
  });
}

export function useApplicablePolicy(productType: CreditProductType) {
  return useQuery({
    queryKey: ['credit-policy-applicable', productType],
    queryFn: () =>
      api
        .get<CreditPolicy>(`/credit/policies/product/${productType}`)
        .then((r) => r.data),
    enabled: !!productType,
  });
}

export function useCreateCreditPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCreditPolicyInput) =>
      api.post<CreditPolicy>('/credit/policies', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-policies'] });
    },
  });
}

export function useUpdateCreditPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateCreditPolicyInput>;
    }) =>
      api.put<CreditPolicy>(`/credit/policies/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-policies'] });
    },
  });
}

export function useDeleteCreditPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<CreditPolicy>(`/credit/policies/${id}`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-policies'] });
    },
  });
}

export function useValidateEligibility() {
  return useMutation({
    mutationFn: ({
      policyId,
      customerId,
      amount,
    }: {
      policyId: string;
      customerId: string;
      amount: number;
    }) =>
      api
        .post<EligibilityResult>(`/credit/policies/${policyId}/validate`, {
          customerId,
          amount,
        })
        .then((r) => r.data),
  });
}

// ==================== Loan Simulation ====================

export function useLoanSimulation() {
  return useMutation({
    mutationFn: (params: SimulationParams & { tenantId?: string }) =>
      api
        .post<SimulationResult>('/credit/simulate', params, {
          params: { tenantId: params.tenantId },
        })
        .then((r) => r.data),
  });
}

export function useAmortizationSchedule() {
  return useMutation({
    mutationFn: (params: SimulationParams & { tenantId?: string }) =>
      api
        .post<AmortizationSchedule>('/credit/simulate/schedule', params, {
          params: { tenantId: params.tenantId },
        })
        .then((r) => r.data),
  });
}

// ==================== Loans ====================

export function useLoans(filters?: LoanFilters) {
  return useQuery({
    queryKey: ['loans', filters],
    queryFn: () =>
      api
        .get<PaginatedLoans>('/credit/loans', { params: filters })
        .then((r) => r.data),
  });
}

export function useMyLoans() {
  return useQuery({
    queryKey: ['my-loans'],
    queryFn: () => api.get<Loan[]>('/credit/loans/my').then((r) => r.data),
  });
}

export function useLoan(loanId: string, includeInstallments = false) {
  return useQuery({
    queryKey: ['loan', loanId, includeInstallments],
    queryFn: () =>
      api
        .get<Loan>(`/credit/loans/${loanId}`, {
          params: { includeInstallments },
        })
        .then((r) => r.data),
    enabled: !!loanId,
  });
}

export function useLoanInstallments(loanId: string) {
  return useQuery({
    queryKey: ['loan-installments', loanId],
    queryFn: () =>
      api.get(`/credit/loans/${loanId}/installments`).then((r) => r.data),
    enabled: !!loanId,
  });
}

export function useRequestLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoanRequestInput) =>
      api.post<Loan>('/credit/loans', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['my-loans'] });
    },
  });
}

export function useApproveLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      loanId,
      notes,
      approvedAmount,
    }: {
      loanId: string;
      notes?: string;
      approvedAmount?: number;
    }) =>
      api
        .post<Loan>(`/credit/loans/${loanId}/approve`, { notes, approvedAmount })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useRejectLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ loanId, reason }: { loanId: string; reason: string }) =>
      api
        .post<Loan>(`/credit/loans/${loanId}/reject`, { reason })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useDisburseLoan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (loanId: string) =>
      api.post<Loan>(`/credit/loans/${loanId}/disburse`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useEarlyPayoff(loanId: string) {
  return useQuery({
    queryKey: ['early-payoff', loanId],
    queryFn: () =>
      api
        .get<EarlyPayoffResult>(`/credit/loans/${loanId}/payoff`)
        .then((r) => r.data),
    enabled: !!loanId,
  });
}

export function useExecuteEarlyPayoff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ loanId, amount }: { loanId: string; amount: number }) =>
      api
        .post<Loan>(`/credit/loans/${loanId}/payoff`, { amount })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['my-loans'] });
    },
  });
}

// ==================== Collection ====================

export function useCollectionRules(isActive?: boolean) {
  return useQuery({
    queryKey: ['collection-rules', isActive],
    queryFn: () =>
      api
        .get<CollectionRule[]>('/credit/collection/rules', {
          params: { isActive },
        })
        .then((r) => r.data),
  });
}

export function useCreateCollectionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCollectionRuleInput) =>
      api
        .post<CollectionRule>('/credit/collection/rules', data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
    },
  });
}

export function useUpdateCollectionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateCollectionRuleInput>;
    }) =>
      api
        .put<CollectionRule>(`/credit/collection/rules/${id}`, data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-rules'] });
    },
  });
}

export function useExecuteCollectionRules() {
  return useMutation({
    mutationFn: () =>
      api.post('/credit/collection/execute').then((r) => r.data),
  });
}

export function useCreateRenegotiation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      loanId,
      data,
    }: {
      loanId: string;
      data: CreateRenegotiationInput;
    }) =>
      api
        .post<Renegotiation>(`/credit/collection/loans/${loanId}/renegotiate`, data)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useAcceptRenegotiation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (renegotiationId: string) =>
      api
        .post<Renegotiation>(
          `/credit/collection/renegotiations/${renegotiationId}/accept`
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useProcessPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      installmentId,
      amount,
      transactionId,
    }: {
      installmentId: string;
      amount: number;
      transactionId?: string;
    }) =>
      api
        .post(`/credit/collection/installments/${installmentId}/payment`, {
          amount,
          transactionId,
        })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      queryClient.invalidateQueries({ queryKey: ['loan-installments'] });
    },
  });
}

// ==================== Reports ====================

export function useAgingReport(date?: string) {
  return useQuery({
    queryKey: ['aging-report', date],
    queryFn: () =>
      api
        .get<AgingReport>('/credit/collection/reports/aging', {
          params: { date },
        })
        .then((r) => r.data),
  });
}

export function usePDDReport(date?: string) {
  return useQuery({
    queryKey: ['pdd-report', date],
    queryFn: () =>
      api
        .get<PDDReport>('/credit/collection/reports/pdd', { params: { date } })
        .then((r) => r.data),
  });
}

// ==================== Dashboard ====================

export function useCreditDashboard(filters?: DashboardFilters) {
  return useQuery({
    queryKey: ['credit-dashboard', filters],
    queryFn: () =>
      api
        .get<CreditDashboard>('/credit/dashboard', { params: filters })
        .then((r) => r.data),
  });
}

export function usePortfolioEvolution(filters?: DashboardFilters) {
  return useQuery({
    queryKey: ['portfolio-evolution', filters],
    queryFn: () =>
      api
        .get('/credit/dashboard/evolution', { params: filters })
        .then((r) => r.data),
  });
}

export function useProductDistribution() {
  return useQuery({
    queryKey: ['product-distribution'],
    queryFn: () =>
      api.get('/credit/dashboard/products').then((r) => r.data),
  });
}

export function useCollectionMetrics() {
  return useQuery({
    queryKey: ['collection-metrics'],
    queryFn: () =>
      api.get('/credit/dashboard/collection').then((r) => r.data),
  });
}

// ==================== Credit Products ====================

export interface ProductDefaults {
  type: CreditProductType;
  minAmount: number;
  maxAmount: number;
  minInstallments: number;
  maxInstallments: number;
  requiresCollateral: boolean;
  description: string;
  maxPayrollPercentage?: number;
  discountRate?: number;
  requiresPJ?: boolean;
  minTransactionHistory?: number;
}

export interface PersonalLoanValidation {
  valid: boolean;
  reasons: string[];
  maxAmount?: number;
}

export interface PayrollLoanValidation {
  valid: boolean;
  reasons: string[];
  maxInstallment?: number;
  netSalary?: number;
  existingDeductions?: number;
}

export interface ReceivablesAdvanceValidation {
  valid: boolean;
  reasons: string[];
  totalAmount?: number;
  advanceAmount?: number;
  discount?: number;
  charges?: any[];
}

export interface WorkingCapitalValidation {
  valid: boolean;
  reasons: string[];
  maxAmount?: number;
  transactionHistory?: {
    months: number;
    averageMonthlyVolume: number;
    totalVolume: number;
  };
}

export function useCreditProducts() {
  return useQuery({
    queryKey: ['credit-products'],
    queryFn: () =>
      api.get<ProductDefaults[]>('/credit/products').then((r) => r.data),
  });
}

export function useCreditProductDefaults(productType: CreditProductType) {
  return useQuery({
    queryKey: ['credit-product-defaults', productType],
    queryFn: () =>
      api
        .get<ProductDefaults>(`/credit/products/${productType}`)
        .then((r) => r.data),
    enabled: !!productType,
  });
}

export function useValidatePersonalLoan() {
  return useMutation({
    mutationFn: (data: { amount: number; installments: number }) =>
      api
        .post<PersonalLoanValidation>('/credit/products/personal-loan/validate', data)
        .then((r) => r.data),
  });
}

export function useValidatePayrollLoan() {
  return useMutation({
    mutationFn: (data: { amount: number; installments: number }) =>
      api
        .post<PayrollLoanValidation>('/credit/products/payroll-loan/validate', data)
        .then((r) => r.data),
  });
}

export function useValidateReceivablesAdvance() {
  return useMutation({
    mutationFn: (data: { chargeIds: string[] }) =>
      api
        .post<ReceivablesAdvanceValidation>('/credit/products/receivables-advance/validate', data)
        .then((r) => r.data),
  });
}

export function useValidateWorkingCapital() {
  return useMutation({
    mutationFn: (data: { entityId: string; amount: number; installments: number }) =>
      api
        .post<WorkingCapitalValidation>('/credit/products/working-capital/validate', data)
        .then((r) => r.data),
  });
}

export function useConvertPayrollToPersonal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (loanId: string) =>
      api
        .post<Loan>(`/credit/products/payroll-loan/${loanId}/convert`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}

export function useProcessReceivablesAdvance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ loanId, chargeIds }: { loanId: string; chargeIds: string[] }) =>
      api
        .post<Loan>(`/credit/products/receivables-advance/${loanId}/process`, { chargeIds })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
    },
  });
}
